import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { ErrorState, Skel, SkeletonRegion, SuccessState } from '../components/ui/PageState';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { HOME_ROUTE } from '../lib/homeRoute';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { user, loading: authLoading, passwordRecovery, clearPasswordRecovery } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (passwordRecovery || user) {
      setReady(true);
      return;
    }

    // Hash/query tokens may still be exchanging — give supabase-js a brief window.
    const timer = window.setTimeout(() => {
      setReady(true);
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [authLoading, passwordRecovery, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      clearPasswordRecovery();
      setDone(true);
      window.setTimeout(() => navigate(HOME_ROUTE, { replace: true }), 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Não foi possível redefinir a senha.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !ready) {
    return (
      <div className="glass-card">
        <SkeletonRegion label="Validando o link de redefinição…">
          <div className="sk-stack" style={{ alignItems: 'center', marginBottom: '1.5rem' }}>
            <Skel className="sk-circle" w={56} h={56} />
            <Skel w="60%" h={26} />
            <Skel w="80%" h={14} />
          </div>
          <div className="sk-stack">
            <Skel h={46} style={{ borderRadius: 8 }} />
            <Skel h={46} style={{ borderRadius: 8 }} />
            <Skel h={46} style={{ borderRadius: 8 }} />
          </div>
        </SkeletonRegion>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="glass-card">
        <ErrorState
          flat
          headingLevel={1}
          title="Link inválido ou expirado"
          message="Links de redefinição valem por pouco tempo. Peça um novo na tela de login."
          actions={<Link to="/login" className="explore-btn is-primary">Pedir novo link no login</Link>}
        />
      </div>
    );
  }

  if (done) {
    return (
      <div className="glass-card">
        <SuccessState
          flat
          headingLevel={1}
          title="Senha atualizada"
          text="Estamos levando você para o app."
          actions={<Link to={HOME_ROUTE} replace className="explore-btn is-primary">Ir para o app agora</Link>}
        />
      </div>
    );
  }

  return (
    <div className="glass-card">
      <div className="auth-brand">
        <img src="/assets/icon-mark.png" alt="Seu.video" className="auth-brand-mark" />
      </div>
      <h1>Redefinir senha</h1>
      <p className="subtitle">Escolha uma nova senha para {user.email}.</p>

      {error && (
        <div className="error-message" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="reset-password">Nova senha</label>
          <input
            id="reset-password"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoFocus
          />
          <span className="form-hint">Mínimo de 6 caracteres.</span>
        </div>

        <div className="form-group">
          <label htmlFor="reset-confirm">Confirmar nova senha</label>
          <input
            id="reset-confirm"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Salvando…' : 'Salvar nova senha'}
        </button>
      </form>

      <div className="auth-link">
        <Link to="/login">Voltar ao login</Link>
      </div>
    </div>
  );
}
